import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Trophy, Play, ArrowRight, Flame, UserPlus } from "lucide-react";

const CATEGORY_COLORS = {
  Talk: "#007AFF", Music: "#FF3B30", Sports: "#32D74B", Podcast: "#FFD60A",
  News: "#AF52DE", Comedy: "#FF9500", Interview: "#5AC8FA", "Live Event": "#FF2D92",
};

function formatCompact(n) {
  if (n == null) return "—";
  if (n >= 1e9) return (n / 1e9).toFixed(1).replace(/\.0$/, "") + "B";
  if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, "") + "K";
  return String(n);
}

/**
 * Home page top-5 leaderboard widget. Auto-hides if there is no ranking data yet
 * (e.g. brand-new deployment with no saves/ratings). Refreshes every 60s.
 */
export default function TopShowsWidget() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/leaderboard/top?limit=5");
        if (!alive) return;
        setRows(data.rows || []);
      } catch {
        if (alive) setRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Hide the widget until at least one show has a report card (collab shows are never ranked).
  if (loading || rows.length === 0) return null;

  return (
    <section className="border-t border-[var(--ff-border)] bg-[var(--ff-bg)]" data-testid="home-top-shows">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-6">
          <div>
            <div className="label-eyebrow mb-4 text-[var(--ff-red)] flex items-center gap-2">
              <Trophy size={14} /> ▮ TOP OF THE NETWORK
            </div>
            <h2 className="font-display text-5xl lg:text-6xl">REPORT CARD LEADERS.</h2>
            <p className="text-[var(--ff-text-dim)] mt-3 max-w-lg">
              Ranked by each show&apos;s latest official report card.
            </p>
          </div>
          <Link to="/leaderboard" className="btn-ghost" data-testid="home-leaderboard-cta">
            Full Leaderboard <ArrowRight size={14} className="inline ml-1" />
          </Link>
        </div>

        <div className="grid gap-px bg-[var(--ff-border)]" data-testid="home-top-shows-rows">
          {rows.map((r, i) => {
            const color = CATEGORY_COLORS[r.category] || "#FF3B30";
            return (
              <Link
                key={r.id}
                to={`/shows/${r.id}`}
                className="bg-[var(--ff-surface)] hover:bg-[var(--ff-surface-hover)] transition-colors grid grid-cols-[auto_64px_1fr_auto] md:grid-cols-[auto_80px_1fr_auto_auto_auto] gap-4 md:gap-6 items-center p-4 md:p-5"
                data-testid={`home-top-show-${i}`}
              >
                <div className="font-display text-3xl md:text-4xl w-8 text-[var(--ff-red)]">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div
                  className="relative aspect-square w-14 md:w-16 overflow-hidden"
                  style={{ background: `linear-gradient(135deg, ${color}44, ${color}11)` }}
                >
                  {r.thumbnail_url ? (
                    <img src={r.thumbnail_url} alt={r.title} className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center font-display text-lg opacity-40" style={{ color }}>
                      {r.title?.split(" ").map((w) => w[0]).slice(0, 3).join("")}
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-display text-lg md:text-2xl truncate">{r.title}</div>
                    {r.is_trending && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-[var(--ff-red)]/15 border border-[var(--ff-red)]/40 text-[var(--ff-red)] font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] shrink-0"
                        title={`Views grew ${r.view_growth_pct}% in the last 7 days`}
                        data-testid={`home-trending-${r.id}`}
                      >
                        <Flame size={11} /> Trending
                      </span>
                    )}
                  </div>
                  {r.category && (
                    <div className="label-eyebrow mt-1" style={{ color }}>{r.category}</div>
                  )}
                </div>
                <div className="hidden md:flex items-center gap-1.5 min-w-[70px] justify-end" title="Views (report card)">
                  <Play size={12} className="text-[#FF0000]" fill={r.view_count != null ? "#FF0000" : "none"} />
                  <span className="font-display text-xl" data-testid={`home-top-show-views-${i}`}>{formatCompact(r.view_count)}</span>
                </div>
                <div className="hidden md:flex items-center gap-1.5 min-w-[70px] justify-end" title="Subscribers (report card)">
                  <UserPlus size={14} className="text-[var(--ff-red)]" />
                  <span className="font-display text-xl">{formatCompact(r.subscriber_count)}</span>
                </div>
                <div className="flex flex-col items-end min-w-[60px]">
                  <span className="label-eyebrow text-[var(--ff-red)]">Score</span>
                  <span className="font-display text-xl" data-testid={`home-top-show-score-${i}`}>{Math.round(r.overall_score * 100)}</span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
